// API Client
import EpdServiceClient, { get, delete_, put } from "../../api/EpdServiceClient";
import { Material } from "@/types/Views/MaterialDatabase/Material";
import { NewMaterial } from "@/types/Views/CreateMaterial/NewMaterial";
import { GetMaterialQuery } from "@/graphql";

// API Path
const path = "api/material/";
const pathV2 = "api/material/v2/";

async function getTenantsForMaterialSearch() {
  return get(`${path}search/tenants`);
}

async function getMaterial(id: string): Promise<Material> {
  return get(`${path}${id}`);
}

async function deleteMaterial(id: string) {
  return delete_(`${path}${id}`);
}

async function updateMaterial(material: NewMaterial) {
  return put(`${path}${material.id}`, material);
}

async function getMaterialV2(id: string): Promise<GetMaterialQuery["material"]> {
  return get(`${pathV2}${id}`);
}

export { getTenantsForMaterialSearch, getMaterial, deleteMaterial, updateMaterial, getMaterialV2 };

//GET
export async function getAllMaterials(pageNumber?: number, pageSize?: number) {
  const response = await EpdServiceClient.get("api/material/GetMaterialsByFilter", { params: { pageNumber, pageSize } });

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

export async function getMaterialsByFilterAndSearch({
  custom,
  datasources,
  forms,
  indicator,
  internalId,
  isConstruction,
  owners,
  pageNumber,
  pageSize,
  search,
  tags,
  category,
  tenants,
  types,
  minValidTo,
}: {
  custom?: boolean;
  datasources: string[];
  forms: string[];
  indicator?: string[];
  internalId?: string[];
  isConstruction?: boolean;
  owners: string[];
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  tags: string[];
  category: string[];
  tenants: string[];
  types: string[];
  minValidTo?: string;
}) {
  const requestBody = {
    dataSource: datasources,
    form: forms,
    indicator: indicator,
    internalId: internalId || undefined,
    isConstruction: isConstruction ?? false, // Use nullish coalescing for optional boolean
    isCustom: custom || undefined,
    owner: owners,
    pageNumber: pageNumber || undefined,
    pageSize: pageSize || undefined,
    search: search || undefined,
    tags,
    category,
    tenantIds: tenants,
    type: types,
    minValidTo: minValidTo,
  };

  // Remove undefined values from the request body
  let cleanedBody = Object.fromEntries(Object.entries(requestBody).filter(([_, value]) => value !== undefined));

  // Remove empty arrays from the request body
  cleanedBody = Object.fromEntries(
    Object.entries(cleanedBody).filter(([_, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return true;
    })
  );

  const response = await EpdServiceClient.post("api/material/search", cleanedBody);

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

// Wire-format payload for POST api/material/search: these are the exact keys the endpoint
// expects (dataSource, form, owner, tenantIds, type, collectionIds). The Library toolbars build
// this shape directly, unlike the V1 helper above which translates friendly names to these keys.
export type MaterialSearchPayload = {
  isCustom?: boolean;
  dataSource?: string[];
  form?: string[];
  indicator?: string[];
  internalId?: string[];
  collectionIds?: string[];
  isConstruction?: boolean;
  owner?: string[];
  search?: string;
  tags?: string[];
  category?: string[];
  tenantIds?: string[];
  type?: string[];
  declaredUnit?: string[];
  minValidTo?: string;
  pageNumber?: number;
  pageSize?: number;
};

export async function getMaterialsByFilterAndSearchV2(payload: MaterialSearchPayload) {
  const response = await EpdServiceClient.post("api/material/search", payload);

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

export async function getSingleMaterial(id?: string) {
  if (id === undefined) return;
  const response = await EpdServiceClient.get(`api/material/${id}`);
  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

export async function getMultipleMaterials(ids: string[]) {
  const queryString = ids.map((id) => `ids=${encodeURIComponent(id)}`).join("&");
  const response = await EpdServiceClient.get(`api/material/?${queryString}`);
  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

export async function fetchTagsTypesForms() {
  const response = await EpdServiceClient.get("api/material/TagsTypesForms");

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}
export async function getDataSources() {
  const response = await EpdServiceClient.get("api/DataSource");

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}
export async function fetchIndicatorEnums() {
  const response = await EpdServiceClient.get("api/material/indicatorenums");

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}
export async function fetchIndicatorTypeNames() {
  const response = await EpdServiceClient.get("api/material/indicatorTypeNames");

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

export async function fetchMassUnitEnum() {
  const response = await EpdServiceClient.get("api/material/declaredmassunitenum");

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

export async function fetchStageStateEnum() {
  const response = await EpdServiceClient.get("api/material/stagestateenum");

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

export async function fetchStageTypeEnums() {
  const response = await EpdServiceClient.get("api/material/stagetypeenum");

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

export async function fetchEpdSpecificationForm() {
  const response = await EpdServiceClient.get("api/material/epdspecificationform");

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

export async function fetchEpdProductIndustryType() {
  const response = await EpdServiceClient.get("api/material/epdproductindustrytype");

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

export async function fetchDeclaredUnitEnums() {
  const response = await EpdServiceClient.get("api/material/declaredunitenum");

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

//PUT
export async function addMaterialToFavorites(id: any) {
  const response = await EpdServiceClient.put("api/material/favorites/add", {
    epdId: id,
    lifetime: 0,
  });

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

export async function removeMaterialFromFavorites(id: any) {
  const response = await EpdServiceClient.put("api/material/favorites/remove", id);

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

export async function getMaterialFavorites() {
  const response = await EpdServiceClient.get("/api/material/favorites");

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

//POST
export async function createMaterial(material: any) {
  const response = await EpdServiceClient.post("api/material/create", material);

  if (response.status === 200) {
    return response.data;
  } else {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}
